import React, {useEffect, useReducer, useContext} from "react";
import axios from "@common/axios";
import {Button, Alert, Row} from "antd";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import CircularLoader from "@common/CircularLoader";
import {WalkthroughStep, WalkthroughSummary} from "./WalkthroughStep";
import {BrandContext} from "@context/BrandContext";
import AdminIdTag, {ADMIN_ID_TAG_TYPES} from "@common/AdminIdTag";
import {ContentContext} from "@context/ContentContext";

const WALKTHROUGH_VIEWS = {
    INTRO: 'intro',
    STEP: 'step',
    SUMMARY: 'summary'
};
const WALKTHROUGH_ACTIONS = {
    LOAD_START: 'LOAD_START',
    LOAD_SUCCESS: 'LOAD_SUCCESS',
    LOAD_ERROR: 'LOAD_ERROR',
    START_WALKTHROUGH: 'START_WALKTHROUGH',
    RESTART_WALKTHROUGH: 'RESTART_WALKTHROUGH',
    NEXT_STEP: 'NEXT_STEP',
    SHOW_SUMMARY: 'SHOW_SUMMARY',
    UPDATE_WALKTHROUGH_COMPLETE_BTN_LABEL: 'UPDATE_WALKTHROUGH_COMPLETE_BTN_LABEL'
};
const walkthroughInitialState = {
    isLoading: true,
    error: null,
    question: null,
    stepNo: 1,
    stepTotal: '...',
    view: WALKTHROUGH_VIEWS.INTRO,
    walkthroughCompleteBtnLabel: "Continue"
};

function walkthroughReducer(state, action) {
    switch (action.type) {
        case WALKTHROUGH_ACTIONS.LOAD_START:
            return {...walkthroughInitialState, isLoading: true};
        case WALKTHROUGH_ACTIONS.LOAD_SUCCESS:
            if (action.payload.loadIntoSummary) {}
            return {
                ...state,
                isLoading: false,
                question: action.payload.question,
                stepTotal: action.payload.question.childQuestionCount,
                ...(action.payload.loadIntoSummary && {
                    view: WALKTHROUGH_VIEWS.SUMMARY
                }),
            };
        case WALKTHROUGH_ACTIONS.LOAD_ERROR:
            return {...state, isLoading: false, error: action.payload};
        case WALKTHROUGH_ACTIONS.NEXT_STEP:
            return {...state, stepNo: state.stepNo + 1};
        case WALKTHROUGH_ACTIONS.START_WALKTHROUGH:
            return {...state, view: WALKTHROUGH_VIEWS.STEP};
        case WALKTHROUGH_ACTIONS.RESTART_WALKTHROUGH:
            return {...state, stepNo: walkthroughInitialState.stepNo, view: WALKTHROUGH_VIEWS.INTRO};
        case WALKTHROUGH_ACTIONS.SHOW_SUMMARY:
            return {...state, stepNo: state.stepTotal, view: WALKTHROUGH_VIEWS.SUMMARY};
        case WALKTHROUGH_ACTIONS.UPDATE_WALKTHROUGH_COMPLETE_BTN_LABEL:
            return {...state, walkthroughCompleteBtnLabel: action.payload};
        default:
            return state;
    }
}

const WalkthroughCore = props => {
    const {
        questionId,
        conceptId,
        displayHeader = true,
        showBackBtn = false,
        showRestartBtn = false,
        walkthroughCompleteBtnLabel,
        showWalkthroughCompleteBtn = false,
        loadIntoSummary = false,
        className,
        onBack,
        onWalkthroughComplete,
        StepComponent,
        stepComponentProps = {},
    } = props;
    const [state, dispatch] = useReducer(walkthroughReducer, walkthroughInitialState);
    const brandConfig = useContext(BrandContext);
    const startBtnRef = React.useRef();
    const {content} = useContext(ContentContext);

    useEffect(() => {
        let isActive = true;
        const loadWalkthrough = async () => {
            dispatch({type: WALKTHROUGH_ACTIONS.LOAD_START});
            try {
                const response = await axios.get('/web/ui/walkthrough/load', {
                    params: {questionId: questionId, conceptId: conceptId}
                });
                if (isActive) dispatch({type: WALKTHROUGH_ACTIONS.LOAD_SUCCESS, payload: {loadIntoSummary: loadIntoSummary, question: response.data}});
            } catch (error) {
                if (isActive) dispatch({type: WALKTHROUGH_ACTIONS.LOAD_ERROR, payload: 'Failed to load walkthrough'});
            }
        };
        loadWalkthrough();

        return () => {isActive = false;};
    }, [questionId, conceptId]);

    useEffect(() => {
        if (!state.isLoading && startBtnRef.current) {
            startBtnRef.current.focus({focusVisible: false, preventScroll: true});
        }
    }, [state.isLoading]);

    useEffect(() => {
        if (walkthroughCompleteBtnLabel) dispatch({type: WALKTHROUGH_ACTIONS.UPDATE_WALKTHROUGH_COMPLETE_BTN_LABEL, payload: walkthroughCompleteBtnLabel});
    }, [walkthroughCompleteBtnLabel]);

    const WalkthroughCompleteBtn = () => {
        if (!showWalkthroughCompleteBtn) return null;
        return (
            <Button
                type="primary"
                aria-label="Continue to next section"
                onClick={() => onWalkthroughComplete?.()}
            >
                {state.walkthroughCompleteBtnLabel}
            </Button>
        )
    }

    return (
        <section className={`${className ? className + " " : ""}`} aria-labelledby="walkthrough-title">
            <AdminIdTag
                id={questionId}
                type={ADMIN_ID_TAG_TYPES.QUESTION}
            />

            <hr className="margin-t--l margin-b--m" />

            <Row justify="space-between" align="middle">
                {displayHeader ? (
                    <h3 id="walkthrough-title" style={{ margin: 0 }}>
                        {brandConfig.walkthroughName} {state.question?.walkthroughIndex}
                    </h3>
                ) : ( // this offsets the missing heading in the flex.
                    <span></span>
                )}
                {(showRestartBtn || showBackBtn) && (
                    <Row justify="end" className="mediumGap">
                        {showRestartBtn && state.view !== WALKTHROUGH_VIEWS.INTRO && (
                            <Button onClick={() => dispatch({type: WALKTHROUGH_ACTIONS.RESTART_WALKTHROUGH})}>
                                Restart
                            </Button>
                        )}
                        {showBackBtn && (
                            <Button onClick={() => onBack?.()}>
                                Back
                            </Button>
                        )}
                    </Row>
                )}
            </Row>

            {state.isLoading && (
                <Row>
                    <CircularLoader displayInElement={true} />
                </Row>
            )}
            {state.error && (
                <Alert type="error" title={state.error} role="alert" aria-live="assertive" />
            )}
            {state?.question && (
                <>
                    {state.question.formattedInstructions && (
                        <MathJaxHTMLContent
                            className={'margin-t--m ' + content?.currentSyllabus?.syllabusContentClass}
                            html={state.question.formattedInstructions}
                            showLoader={false}
                        />
                    )}
                    {state.question.initialDisplay && (
                        <MathJaxHTMLContent
                            className={'margin-t--m ' + content?.currentSyllabus?.syllabusContentClass}
                            html={state.question.initialDisplay}
                            showLoader={false}
                        />
                    )}
                </>
            )}
            {state.view === WALKTHROUGH_VIEWS.INTRO && (
                <Row className="margin-t--m mediumGap">
                    <Button
                        ref={startBtnRef}
                        type="primary"
                        onClick={() => dispatch({type: WALKTHROUGH_ACTIONS.START_WALKTHROUGH})}
                        aria-label="Continue to the next step"
                    >
                        Continue
                    </Button>
                </Row>
            )}
            {state.view === WALKTHROUGH_VIEWS.STEP && (
                <>
                    <hr className="margin-t--l" />
                    <StepComponent
                        key={questionId}
                        {...stepComponentProps}
                        questionId={questionId}
                        conceptId={conceptId}
                        stepNo={state.stepNo}
                        stepTotal={state.stepTotal}
                        onNextStep={() => dispatch({type: WALKTHROUGH_ACTIONS.NEXT_STEP})}
                        setShowSummary={() => dispatch({type: WALKTHROUGH_ACTIONS.SHOW_SUMMARY})}
                        setWalkthroughCompleteBtnLabel={(label) => dispatch({type: WALKTHROUGH_ACTIONS.UPDATE_WALKTHROUGH_COMPLETE_BTN_LABEL, payload: label})}
                        WalkthroughCompleteBtn={WalkthroughCompleteBtn}
                    />
                </>
            )}
            {state.view === WALKTHROUGH_VIEWS.SUMMARY && (
                <>
                    <hr className="margin-t--l" />
                    <WalkthroughSummary
                        question={state.question}
                        completed={state.stepNo === state.stepTotal}
                        WalkthroughCompleteBtn={WalkthroughCompleteBtn}
                    />
                </>
            )}
        </section>
    )
}

const Walkthrough = props => {
    return (
        <WalkthroughCore
            {...props}
            StepComponent={WalkthroughStep}
        />
    )
};

export {Walkthrough as default, WalkthroughCore};